import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { OutreachNoteKindSchema, type Admin } from "@lumika/shared";
import {
  Button,
  Section,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea,
  toast,
} from "@lumika/ui";
import { adminApi } from "../../api/admin";
import { buildAdminNames, formatDateTime } from "./outreach-ui";

const NOTE_KIND_LABELS: Record<(typeof OutreachNoteKindSchema.options)[number], string> = {
  note: "Note",
  call: "Call",
  email: "Email",
  linkedin: "LinkedIn",
  meeting: "Meeting",
};

type Props = {
  prospectId: string;
  admins: Admin[];
};

/** Append-only activity log: add a note and list all prior notes. */
export function ActivityLog({ prospectId, admins }: Props) {
  const qc = useQueryClient();
  const [body, setBody] = useState("");
  const [kind, setKind] = useState<(typeof OutreachNoteKindSchema.options)[number]>("note");
  const adminNames = useMemo(() => buildAdminNames(admins), [admins]);

  const notesQuery = useQuery({
    queryKey: ["outreach", "notes", prospectId],
    queryFn: () => adminApi.listOutreachNotes(prospectId),
  });

  const addMutation = useMutation({
    mutationFn: () => adminApi.addOutreachNote(prospectId, { kind, body }),
    onSuccess: async () => {
      setBody("");
      setKind("note");
      await qc.invalidateQueries({ queryKey: ["outreach", "notes", prospectId] });
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const notes = notesQuery.data ?? [];

  return (
    <Section title="Activity">
      <div className="grid gap-3">
        <Textarea
          value={body}
          onChange={(e) => setBody(e.target.value)}
          rows={3}
          placeholder="Log a call, email, or note…"
          aria-label="Log activity note"
        />
        <div className="flex items-center justify-between gap-2">
          <Select value={kind} onValueChange={(v) => setKind(v as typeof kind)}>
            <SelectTrigger className="w-36">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {OutreachNoteKindSchema.options.map((k) => (
                <SelectItem key={k} value={k}>
                  {NOTE_KIND_LABELS[k]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            size="sm"
            onClick={() => addMutation.mutate()}
            disabled={!body.trim() || addMutation.isPending}
          >
            {addMutation.isPending ? "Adding…" : "Add"}
          </Button>
        </div>

        <div className="mt-2 grid gap-3">
          {notesQuery.isLoading ? (
            <p className="text-sm text-muted-foreground">Loading activity…</p>
          ) : notesQuery.isError ? (
            <p className="text-sm text-destructive">
              Couldn&rsquo;t load activity — refresh to try again.
            </p>
          ) : notes.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              No activity yet. Log your first interaction above.
            </p>
          ) : (
            notes.map((note) => (
              <div key={note.id} className="rounded-lg border bg-card p-3">
                <div className="mb-1 flex items-center gap-2 text-xs text-muted-foreground">
                  <span className="rounded-full border bg-muted px-1.5 py-0.5 font-medium uppercase tracking-wide text-[10px]">
                    {NOTE_KIND_LABELS[note.kind]}
                  </span>
                  <span>
                    {note.authorAdminId ? (adminNames.get(note.authorAdminId) ?? "Unknown") : "—"}
                  </span>
                  <span className="text-muted-foreground/40">·</span>
                  <span>{formatDateTime(note.createdAt)}</span>
                </div>
                <p className="whitespace-pre-wrap text-sm">{note.body}</p>
              </div>
            ))
          )}
        </div>
      </div>
    </Section>
  );
}
